import React, { useState, useEffect } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { Spreadsheet } from "react-spreadsheet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faCompress,
  faExclamationTriangle,
  faExpand,
  faFile,
  faFileAlt,
  faFileCsv,
  faFileExcel,
  faTable,
  faX,
} from "@fortawesome/free-solid-svg-icons";
import { colors } from "../../global";
import { toast } from "react-toastify";

const FileToSpreadsheetModal = ({ file, closeModal }) => {
  const [data, setData] = useState([]);
  const [fileType, setFileType] = useState("");
  const [sheetNames, setSheetNames] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [columnWidths, setColumnWidths] = useState([]);

  const calculateColumnWidths = (data) => {
    if (!data || data.length === 0) return [];

    const maxCols = Math.max(...data.map((row) => row.length));
    const widths = [];

    for (let col = 0; col < maxCols; col++) {
      let maxWidth = 80;

      for (let row = 0; row < Math.min(data.length, 100); row++) {
        const cellValue = data[row][col]?.value || "";
        const cellLength = String(cellValue).length;

        const estimatedWidth = Math.min(Math.max(cellLength * 8 + 24, 80), 300);
        maxWidth = Math.max(maxWidth, estimatedWidth);
      }

      widths.push(maxWidth);
    }

    return widths;
  };

  const handleCSVFileUpload = (file) => {
    setIsLoading(true);
    Papa.parse(file, {
      complete: (result) => {
        const rows = result.data.map((row) =>
          row.map((cell) => ({ value: cell }))
        );
        setData(rows);
        setFileType("CSV");
        setTimeout(() => {
          const widths = calculateColumnWidths(rows);
          setColumnWidths(widths);
          setIsLoading(false);
        }, 100);
      },
      header: false,
    });
  };

  const handleExcelFileUpload = (file) => {
    setIsLoading(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      const ab = e.target.result;
      const wb = XLSX.read(ab, { type: "array" });
      const sheetNames = wb.SheetNames;
      setSheetNames(sheetNames);
      setFileType("Excel");

      if (sheetNames.length > 0) {
        setSelectedSheet(sheetNames[0]);
        const ws = wb.Sheets[sheetNames[0]];

        const rawData = XLSX.utils.sheet_to_json(ws, {
          header: 1,
          blankrows: true,
          defval: "",
        });

        const maxCols = Math.max(...rawData.map((row) => row.length));
        const normalizedData = rawData.map((row) => {
          const filledRow = [...row];
          while (filledRow.length < maxCols) {
            filledRow.push("");
          }
          return filledRow.map((cell) => ({ value: cell }));
        });

        setData(normalizedData.slice(0, 200));
        setTimeout(() => {
          const widths = calculateColumnWidths(normalizedData);
          setColumnWidths(widths);
        }, 100);
      }

      setIsLoading(false);
    };

    reader.readAsArrayBuffer(file);
  };

  const handleSheetChange = (e) => {
    const selectedSheet = e.target.value;
    setSelectedSheet(selectedSheet);
    setIsLoading(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      const ab = e.target.result;
      const wb = XLSX.read(ab, { type: "array" });
      const ws = wb.Sheets[selectedSheet];

      const rawData = XLSX.utils.sheet_to_json(ws, {
        header: 1,
        blankrows: true,
        defval: "",
      });

      const maxCols = Math.max(...rawData.map((row) => row.length));
      const normalizedData = rawData.map((row) => {
        const filledRow = [...row];
        while (filledRow.length < maxCols) {
          filledRow.push("");
        }
        return filledRow.map((cell) => ({ value: cell }));
      });

      setData(normalizedData);
      setIsLoading(false);
      setTimeout(() => {
        const widths = calculateColumnWidths(normalizedData);
        setColumnWidths(widths);
      }, 100);
    };

    reader.readAsArrayBuffer(file);
  };

  useEffect(() => {
    if (file) {
      if (file.name.endsWith(".csv")) {
        handleCSVFileUpload(file);
      } else if (
        file.name.endsWith(".xls") ||
        file.name.endsWith(".xlsx") ||
        file.name.endsWith(".xlsb")
      ) {
        handleExcelFileUpload(file);
      } else {
        toast.error("Invalid file type. Please upload a CSV or Excel file.");
      }
    }
  }, [file]);

  const getFileIcon = () => {
    if (fileType === "CSV") {
      return faFileCsv;
    } else if (fileType === "Excel") {
      return faFileExcel;
    }
    return faFile;
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
      <div
        className={`bg-white shadow-2xl transition-all duration-300 flex flex-col
            ${
              isFullScreen
                ? "w-full h-full rounded-none m-0"
                : "w-full max-w-6xl max-h-[90vh] rounded-lg"
            }`}
        style={{ backgroundColor: colors.white }}
      >
        {/* Modal Header */}
        <div
          className="flex justify-between items-center p-6 border-b-2 rounded-t-lg"
          style={{
            backgroundColor: colors.primary,
            borderColor: colors.secondary,
          }}
        >
          <div className="flex items-center space-x-3">
            <div
              className="flex items-center justify-center w-10 h-10 rounded-full"
              style={{ backgroundColor: colors.accent }}
            >
              <FontAwesomeIcon
                icon={getFileIcon()}
                className="text-white text-lg"
              />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">File Preview</h2>
              {file && (
                <p className="text-sm opacity-90 text-white">{file.name}</p>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsFullScreen(!isFullScreen)}
              className="flex items-center space-x-2 px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg transition-all duration-200 font-medium"
            >
              <FontAwesomeIcon icon={isFullScreen ? faCompress : faExpand} />
              <span className="hidden sm:inline">
                {isFullScreen ? "Exit Full Screen" : "Full Screen"}
              </span>
            </button>
            <button
              onClick={closeModal}
              className="flex items-center justify-center w-10 h-10 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg transition-all duration-200"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faX} />
            </button>
          </div>
        </div>

        <div
          className="flex-1 overflow-auto p-6"
          style={{
            backgroundColor: colors.white,
          }}
        >
          {fileType === "Excel" && sheetNames.length > 0 && (
            <div className="mb-6">
              <label
                htmlFor="sheetSelect"
                className="flex items-center space-x-2 font-semibold mb-3"
                style={{ color: colors.primary }}
              >
                <FontAwesomeIcon icon={faTable} />
                <i className="fas fa-table"></i>
                <span>Select Sheet</span>
              </label>
              <div className="relative">
                <select
                  id="sheetSelect"
                  className="w-full p-3 pr-10 border-2 rounded-lg appearance-none focus:outline-none focus:ring-2 transition-all duration-200"
                  style={{
                    borderColor: colors.gray,
                    focusRingColor: colors.primary,
                  }}
                  value={selectedSheet}
                  onChange={handleSheetChange}
                >
                  {sheetNames.map((sheetName) => (
                    <option key={sheetName} value={sheetName}>
                      {sheetName}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    style={{ color: colors.darkGray }}
                  />
                </div>
              </div>
            </div>
          )}

          {fileType && (
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div
                className="flex items-center gap-2 px-4 py-2 rounded-full text-white font-medium"
                style={{ backgroundColor: colors.secondary }}
              >
                <FontAwesomeIcon icon={getFileIcon()} />
                <span className="whitespace-nowrap">{fileType} File</span>
              </div>

              {data.length > 0 && (
                <div
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-white font-medium"
                  style={{ backgroundColor: colors.accent }}
                >
                  <FontAwesomeIcon icon={faTable} />
                  <span className="whitespace-nowrap">
                    {data.length} rows x {data[0]?.length || 0} columns
                  </span>
                </div>
              )}
            </div>
          )}

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20 space-y-4">
              <div className="relative">
                <div
                  className="animate-spin rounded-full h-12 w-12 border-4 border-solid border-opacity-20"
                  style={{
                    borderColor: colors.primary,
                    borderTopColor: colors.accent,
                  }}
                ></div>
                <FontAwesomeIcon
                  icon={faFileAlt}
                  className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-lg"
                />
              </div>
              <div className="text-center">
                <p
                  className="text-lg font-medium"
                  style={{ color: colors.primary }}
                >
                  Processing file...
                </p>
                <p className="text-sm mt-1" style={{ color: colors.darkGray }}>
                  Please wait while we parse your data
                </p>
              </div>
            </div>
          ) : data.length > 0 ? (
            <div
              className="border-2 rounded-lg overflow-hidden shadow-inner"
              style={{
                borderColor: colors.gray,
                height: isFullScreen ? "calc(100vh - 240px)" : "500px",
              }}
            >
              <div className="h-full overflow-auto bg-white relative">
                <div
                  className="spreadsheet-wrapper h-full w-full"
                  style={{
                    "--column-widths": columnWidths
                      .map((w) => `${w}px`)
                      .join(" "),
                  }}
                >
                  <Spreadsheet
                    data={data}
                    columnLabels={Array.from(
                      { length: Math.max(...data.map((row) => row.length)) },
                      (_, i) =>
                        String.fromCharCode(65 + (i % 26)) +
                        (Math.floor(i / 26) > 0 ? Math.floor(i / 26) : "")
                    )}
                    rowLabels={Array.from({ length: data.length }, (_, i) =>
                      (i + 1).toString()
                    )}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 space-y-4">
              <div
                className="flex items-center justify-center w-16 h-16 rounded-full"
                style={{ backgroundColor: colors.gray }}
              >
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="text-2xl"
                />
              </div>
              <div className="text-center">
                <p
                  className="text-lg font-medium"
                  style={{ color: colors.primary }}
                >
                  No data available
                </p>
                <p className="text-sm mt-1" style={{ color: colors.darkGray }}>
                  The file appears to be empty or could not be processed
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileToSpreadsheetModal;
